import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Trash2, Phone, MessageCircle, RefreshCw } from "lucide-react";

const STATUS_OPTIONS = [
    { value: "new", label: "Yeni" },
    { value: "contacted", label: "Aranıldı" },
    { value: "scheduled", label: "Randevu Verildi" },
    { value: "closed", label: "Tamamlandı" },
    { value: "spam", label: "Spam" },
];

const STATUS_COLOR = {
    new: "bg-cyan-400/10 text-cyan-300 border-cyan-400/30",
    contacted: "bg-amber-400/10 text-amber-300 border-amber-400/30",
    scheduled: "bg-violet-400/10 text-violet-300 border-violet-400/30",
    closed: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
    spam: "bg-zinc-400/10 text-zinc-400 border-zinc-400/30",
};

export default function AdminLeads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/leads");
            setLeads(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const updateStatus = async (id, status) => {
        try {
            const { data } = await api.patch(`/admin/leads/${id}`, { status });
            setLeads((arr) => arr.map((l) => (l.id === id ? data : l)));
            toast.success("Durum güncellendi.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        }
    };

    const remove = async (id) => {
        if (!confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;
        try {
            await api.delete(`/admin/leads/${id}`);
            setLeads((arr) => arr.filter((l) => l.id !== id));
            toast.success("Lead silindi.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        }
    };

    const stats = {
        total: leads.length,
        new: leads.filter((l) => l.status === "new").length,
        contacted: leads.filter((l) => l.status === "contacted").length,
        scheduled: leads.filter((l) => l.status === "scheduled").length,
    };

    return (
        <div className="p-6 md:p-10">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-cyan-300/80 mb-2">
                        Yönetim
                    </p>
                    <h1 className="font-display text-3xl text-white">Lead Talepleri</h1>
                </div>
                <Button
                    variant="outline"
                    onClick={load}
                    className="bg-transparent border-white/10 text-white hover:bg-white/5"
                    data-testid="leads-refresh"
                >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Yenile
                </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {[
                    { l: "Toplam", v: stats.total },
                    { l: "Yeni", v: stats.new },
                    { l: "Aranıldı", v: stats.contacted },
                    { l: "Randevu", v: stats.scheduled },
                ].map((s) => (
                    <div key={s.l} className="bg-[#0E0E0E] border border-white/5 rounded-xl p-4">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{s.l}</p>
                        <p className="font-display text-3xl text-white">{s.v}</p>
                    </div>
                ))}
            </div>

            {loading && <p className="text-zinc-500">Yükleniyor...</p>}

            {!loading && leads.length === 0 && (
                <div className="text-center py-20 bg-[#0E0E0E] border border-white/5 rounded-2xl">
                    <p className="text-zinc-500">Henüz lead yok.</p>
                </div>
            )}

            <div className="space-y-3">
                {leads.map((l) => (
                    <div
                        key={l.id}
                        className="bg-[#0E0E0E] border border-white/5 rounded-xl p-5 hover:border-white/10 transition-colors"
                        data-testid={`lead-row-${l.id}`}
                    >
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <h3 className="font-display text-lg text-white">{l.full_name}</h3>
                                    <span
                                        className={`inline-block text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full border ${STATUS_COLOR[l.status] || ""}`}
                                    >
                                        {STATUS_OPTIONS.find((s) => s.value === l.status)?.label || l.status}
                                    </span>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-3 text-sm text-zinc-400">
                                    <a
                                        href={`tel:${l.phone}`}
                                        className="inline-flex items-center gap-1.5 hover:text-cyan-300"
                                    >
                                        <Phone className="h-3.5 w-3.5" />
                                        {l.phone}
                                    </a>
                                    <a
                                        href={`https://wa.me/${l.phone.replace(/\D/g, "")}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 hover:text-cyan-300"
                                    >
                                        <MessageCircle className="h-3.5 w-3.5" />
                                        WhatsApp
                                    </a>
                                    <span className="text-zinc-500">
                                        {new Date(l.created_at).toLocaleString("tr-TR")}
                                    </span>
                                </div>
                                <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-1 text-xs text-zinc-400">
                                    <p>
                                        <strong className="text-zinc-300">Hizmet:</strong> {l.service_type}
                                    </p>
                                    {l.region && (
                                        <p>
                                            <strong className="text-zinc-300">Bölge:</strong> {l.region}
                                        </p>
                                    )}
                                    {l.color_or_appearance && (
                                        <p>
                                            <strong className="text-zinc-300">Renk/Görünüm:</strong>{" "}
                                            {l.color_or_appearance}
                                        </p>
                                    )}
                                    {l.size && (
                                        <p>
                                            <strong className="text-zinc-300">Boyut:</strong> {l.size}
                                        </p>
                                    )}
                                    {l.duration && (
                                        <p>
                                            <strong className="text-zinc-300">Süre:</strong> {l.duration}
                                        </p>
                                    )}
                                    {l.previous_treatment && (
                                        <p>
                                            <strong className="text-zinc-300">Önceki:</strong>{" "}
                                            {l.previous_treatment}
                                        </p>
                                    )}
                                    <p>
                                        <strong className="text-zinc-300">Tercih:</strong>{" "}
                                        {l.contact_preference}
                                    </p>
                                </div>
                                {l.message && (
                                    <p className="mt-3 text-sm text-zinc-300 bg-[#141414] rounded-lg p-3">
                                        {l.message}
                                    </p>
                                )}
                            </div>
                            <div className="flex md:flex-col gap-2">
                                <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                                    <SelectTrigger
                                        className="w-44 bg-[#141414] border-white/10 text-white"
                                        data-testid={`lead-status-${l.id}`}
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#141414] border-white/10 text-white">
                                        {STATUS_OPTIONS.map((s) => (
                                            <SelectItem key={s.value} value={s.value}>
                                                {s.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => remove(l.id)}
                                    className="bg-transparent border-red-400/20 text-red-400 hover:bg-red-400/10"
                                    data-testid={`lead-delete-${l.id}`}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
